/**
 * NAV-1 — the rail's hover flyout. One component, used by Projects and by the
 * Work Table.
 *
 * It opens when the pointer rests on the rail item for 150ms (a fine pointer
 * only — a finger keeps the item's tap), or on ArrowRight from the focused
 * item. It closes when the pointer leaves both the item and the panel for
 * 250ms, on Escape, on a click inside it, or when the route changes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TOOLS, COMING, routesForYard } from './workTableTools';
import { getCurrentUserId } from '@/utils/currentUser';
import '@/styles/rail-flyout.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OPEN_AFTER = 150;
const CLOSE_AFTER = 250;
const KEEP_FOR = 5 * 60 * 1000;
const SEARCH_FROM = 12;

export const PHASE_LABELS = {
  pre_design: 'Pre-design', programming: 'Programming', concept: 'Concept',
  schematic: 'Schematic', schematic_design: 'Schematic design',
  design_development: 'Design development', construction_documents: 'Construction documents',
  permitting: 'Permitting', bidding: 'Bidding', construction: 'Construction',
  construction_admin: 'Construction admin', closeout: 'Closeout', complete: 'Complete',
};

export const phaseLabel = (phase) => {
  if (!phase) return 'No phase';
  if (PHASE_LABELS[phase]) return PHASE_LABELS[phase];
  const words = String(phase);
  // A phase with no letters in it is not a phase, whatever the row holds.
  if (!/[a-z]/i.test(words)) return 'No phase';
  return words.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
};

export const shortDay = (value) => {
  if (!value) return '';
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return '';
  return when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** The rows a Projects flyout shows: live boards, the freshest first. */
export const projectRows = (list) => (list || [])
  .filter((p) => !p.is_archived)
  .map((p) => ({ ...p, _when: new Date(p.last_activity || p.updated_at || 0).getTime() || 0 }))
  .sort((a, b) => b._when - a._when);

let cached = { at: 0, rows: null };

/** The session's list, dropped — the panel reads it again on the next open. */
export const clearProjectCache = () => { cached = { at: 0, rows: null }; };

export default function RailFlyout({ flyoutKey, anchorRef, open, onClose, ...pointer }) {
  const navigate = useNavigate();
  const location = useLocation();
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const [projects, setProjects] = useState(cached.rows);
  const [yardId, setYardId] = useState(null);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(-1);
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);

  const isProjects = flyoutKey === 'projects';

  // the panel sits beside the item it belongs to
  useEffect(() => {
    if (!open) return;
    const box = anchorRef?.current?.getBoundingClientRect?.();
    if (box) {
      setTop(Math.round(box.top));
      setLeft(Math.round(box.right + 8));
    }
  }, [open, anchorRef]);

  useEffect(() => { if (!open) { setQuery(''); setCursor(-1); } }, [open]);

  // what the flyout needs, read on first open and kept for the session
  useEffect(() => {
    if (!open) return;
    let live = true;
    (async () => {
      if (isProjects) {
        const fresh = cached.rows && Date.now() - cached.at < KEEP_FOR;
        if (fresh) { setProjects(cached.rows); return; }
        const r = await axios.get(`${API}/projects`).catch(() => null);
        if (!live || !r) return;
        const list = Array.isArray(r.data) ? r.data : (r.data?.projects || []);
        cached = { at: Date.now(), rows: list };
        setProjects(list);
      } else if (!yardId) {
        const r = await axios.get(`${API}/work-tables/home/${getCurrentUserId()}`)
          .catch(() => null);
        if (!live || !r) return;
        if (r.data?.id && (r.data.items || []).length > 0) setYardId(r.data.id);
      }
    })();
    return () => { live = false; };
  }, [open, isProjects, yardId]);

  // a new route means the flyout has done its job
  const path = location.pathname + location.search;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => { closeRef.current?.(); }, [path]);

  const routeFor = useMemo(() => routesForYard(yardId), [yardId]);

  const rows = useMemo(() => {
    if (!isProjects) {
      return TOOLS.map(([name, letter, token, desc, shape, key]) => ({
        id: key, key, name, letter, token, desc, shape,
        to: routeFor[key], blocked: COMING.includes(key) || !routeFor[key],
      }));
    }
    const all = projectRows(projects);
    const needle = query.trim().toLowerCase();
    return needle
      ? all.filter((p) => String(p.name || '').toLowerCase().includes(needle))
      : all;
  }, [isProjects, projects, query, routeFor]);

  const footTo = isProjects ? '/projects' : '/work-table';
  const showSearch = isProjects && projectRows(projects).length > SEARCH_FROM;

  const go = useCallback((to) => {
    if (!to) return;
    onClose?.();
    navigate(to);
  }, [navigate, onClose]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
      anchorRef?.current?.focus?.();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next = Math.min(rows.length - 1, Math.max(0, cursor + step));
      setCursor(next);
      const node = panelRef.current?.querySelectorAll('[data-rail-row="true"]')[next];
      node?.focus?.();
    }
  };

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className="km-flyout"
      style={{ top: `${top}px`, left: `${left}px`,
        maxHeight: `calc(100vh - ${top}px - var(--km-s3))` }}
      onKeyDown={onKeyDown}
      onPointerEnter={pointer.onPointerEnter}
      onPointerLeave={pointer.onPointerLeave}
      onClick={(e) => { if (e.target.closest('[data-rail-row="true"]')) return; }}
      data-testid={`rail-flyout-${flyoutKey}`}
    >
      <div className="km-flyout__hd" data-testid={`rail-flyout-head-${flyoutKey}`}>
        {isProjects
          ? `Projects · ${projectRows(projects).length}`
          : `Work Table · ${TOOLS.length} tools`}
      </div>

      {showSearch ? (
        <input
          ref={searchRef}
          className="km-flyout__search"
          placeholder="Find a project"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="rail-flyout-search"
        />
      ) : null}

      <div className="km-flyout__rows">
        {isProjects ? rows.map((p) => (
          <button
            type="button"
            key={p.id}
            className="km-flyout__row"
            data-rail-row="true"
            onClick={() => go(`/projects/${p.id}`)}
            data-testid={`rail-flyout-project-${p.id}`}
          >
            <span className="km-flyout__name">{p.name}</span>
            <span className="km-flyout__micro">
              {`${phaseLabel(p.current_phase)}${shortDay(p.last_activity || p.updated_at)
                ? ` · ${shortDay(p.last_activity || p.updated_at)}` : ''}`}
            </span>
          </button>
        )) : rows.map((t) => (
          <button
            type="button"
            key={t.key}
            className="km-flyout__row km-flyout__row--tool"
            data-rail-row="true"
            disabled={t.blocked}
            style={{ '--kmw-tc': `var(${t.token})` }}
            onClick={() => go(t.to)}
            data-testid={`rail-flyout-tool-${t.key}`}
          >
            <span className={`km-flyout__mark${t.shape === 'round' ? ' km-flyout__mark--round' : ''}`}>
              {t.letter}
            </span>
            <span className="km-flyout__toolwords">
              <span className="km-flyout__name">{t.name}</span>
              <span className="km-flyout__micro">{t.desc}</span>
            </span>
          </button>
        ))}
        {isProjects && rows.length === 0 ? (
          <p className="km-flyout__micro km-flyout__empty" data-testid="rail-flyout-empty">
            {query ? 'Nothing by that name' : 'No projects yet'}
          </p>
        ) : null}
      </div>

      <button
        type="button"
        className="km-flyout__foot"
        data-rail-row="true"
        onClick={() => go(footTo)}
        data-testid={`rail-flyout-foot-${flyoutKey}`}
      >
        {isProjects ? 'All projects' : 'The Work Table'}
      </button>
    </div>
  );
}

/**
 * The timers and the pointer rules, so the rail item only has to say which
 * flyout it owns.
 */
export function useRailFlyout(flyoutKey) {
  const [open, setOpen] = useState(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const anchorRef = useRef(null);

  const fine = () => (typeof window === 'undefined' || !window.matchMedia
    ? true : window.matchMedia('(pointer: fine)').matches);

  const clear = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };

  const rest = () => {
    if (!fine()) return;              // a finger keeps the item's tap
    clear();
    openTimer.current = setTimeout(() => setOpen(true), OPEN_AFTER);
  };

  const leave = () => {
    clear();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_AFTER);
  };

  const close = useCallback(() => { clear(); setOpen(false); }, []);

  const onAnchorKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      clear();
      setOpen(true);
    } else if (e.key === 'Escape') {
      close();
    }
  };

  useEffect(() => clear, []);

  return {
    open,
    anchorRef,
    close,
    anchorProps: {
      ref: anchorRef,
      onPointerEnter: rest,
      onPointerLeave: leave,
      onKeyDown: onAnchorKeyDown,
    },
    panelProps: {
      flyoutKey,
      anchorRef,
      open,
      onClose: close,
      onPointerEnter: clear,
      onPointerLeave: leave,
    },
  };
}
